import { computed, ref } from 'vue'

import localesJson from './en.json'

export type FlatLocaleObject = Record<string, string>
export type ReplacementsMap = Record<string, string[]>

export const useLocales = () => {
  const dictionary = ref(localesJson)

  const quickDictionary = computed(() => {
    return {
      inputs: dictionary.value.placeholder.inputs,
      toast: dictionary.value.toast,
      states: dictionary.value.states,
      barcodeToProduct: dictionary.value.domain.product.enhance.barcodeToProduct,
      validation: dictionary.value.validation
    }
  })

  const localesHelper = (
    _input: string | FlatLocaleObject,
    _replacements?: string[] | ReplacementsMap
  ): string | FlatLocaleObject => {
    // Case 1: _input is a string
    if (typeof _input === 'string') {
      // Ensure _replacements is an array of strings, or default to an empty array
      // The cast `as string[]` is safe due to the overload signature for string inputs.
      const replacementArray = Array.isArray(_replacements) ? (_replacements as string[]) : []
      return replacePlaceholders(_input, replacementArray)
    }

    // Case 2: _input is an object (FlatLocaleObject)
    // At this point, TypeScript knows _input must be FlatLocaleObject due to the type guard
    // and the overall function type signature.
    const currentObjectInput = _input as FlatLocaleObject // More explicit cast for clarity
    const newObj: FlatLocaleObject = { ...currentObjectInput } // Shallow copy

    // Ensure _replacements is a ReplacementsMap object, or default to an empty object
    // The cast `as ReplacementsMap` is safe here due to overload logic when _input is an object.
    const replacementsMap =
      typeof _replacements === 'object' && !Array.isArray(_replacements) && _replacements !== null
        ? (_replacements as ReplacementsMap)
        : {}

    for (const key in newObj) {
      if (Object.prototype.hasOwnProperty.call(newObj, key)) {
        const value = newObj[key]
        if (typeof value === 'string') {
          // Check if there's a specific replacement array for this key in the replacementsMap
          const keySpecificReplacementValues = replacementsMap[key]
          if (Array.isArray(keySpecificReplacementValues)) {
            newObj[key] = replacePlaceholders(value, keySpecificReplacementValues)
          }
          // If no entry for this key, or if it's not an array, the string value remains as is.
        }
        // Non-string values are preserved by the initial shallow copy.
      }
    }
    return newObj
  }

  const replacePlaceholders = (inputString: string, replacementValues: string[]): string => {
    return inputString.replace(/\[\d+]/g, (match: string): string => {
      // Inner callback is also an arrow function
      const indexString = match.slice(1, -1) // Extracts the number from "[n]"
      const index = parseInt(indexString, 10)

      // Check if index is a valid number, non-negative, and within array bounds
      if (!isNaN(index) && index >= 0 && index < replacementValues.length) {
        return String(replacementValues[index]) // Ensure replacement is a string
      }
      // If placeholder is invalid or no replacement exists, return the original placeholder
      return match
    })
  }

  return {
    localesHelper,
    quickDictionary
  }
}
